import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import satAirportImg from "@/assets/sat-airport-night.jpg";
import satAirportVideo from "@/assets/sat-airport-audi-q7-cinematic-v3.mp4.asset.json";

const AirportTransportation = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/airport-transportation#service",
    "name": "San Antonio Airport Car Service",
    "alternateName": "SAT Airport Transfers",
    "serviceType": "Airport Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Luxury airport car service to and from San Antonio International Airport (SAT). Flight tracking, meet & greet, flat rates. Veteran-owned. Book: 210-296-7409.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/airport-transportation",
      "priceCurrency": "USD",
      "lowPrice": "75",
      "highPrice": "100",
      "offerCount": "1",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$75-$100"
  };

  return (
    <>
      <SEO
        title="San Antonio Airport Car Service | SAT Transfers | Rick's Black Car Service"
        description="Luxury airport car service to and from San Antonio International Airport (SAT). Flight tracking, meet & greet, flat rates. Veteran-owned. Book: 210-296-7409."
        canonicalPath="/airport-transportation"
        schema={serviceSchema}
        includeBreadcrumbs={false}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb items={[{ name: "Airport Transportation", url: "/airport-transportation" }]} />
          {/* Hero Section */}
          <section className="relative min-h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                src={satAirportVideo.url}
                poster={satAirportImg}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                disableRemotePlayback
                aria-label="2025 Audi Q7 arriving at San Antonio International Airport"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                San Antonio Airport Car Service (SAT) — Reliable Luxury Transfers
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Premium airport transportation to and from San Antonio International Airport. Flight tracking, meet & greet, and flat-rate pricing — 24/7.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Online Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Your trip starts the moment you leave your door — not when the plane takes off. Rick's Black Car Service provides premium airport transportation to and from San Antonio International Airport (SAT) with the punctuality and professionalism of a 22-year U.S. Army veteran behind the wheel. No waiting for a rideshare that cancels, no crowded shuttles — just a spotless luxury vehicle and a chauffeur who's already there when you land.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Complimentary Flight Tracking on Every Pickup
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    We monitor your flight in real time. If your flight lands early, we're early. If it's delayed two hours, your pickup adjusts automatically — no phone calls, no extra fees, no stress. This is why frequent flyers and business travelers across San Antonio make us their standing airport ride.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Meet & Greet Service at SAT
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    When you land at San Antonio International, head to baggage claim. Your chauffeur will be waiting with a name sign, ready to help with your luggage and walk you to your vehicle. Prefer a faster curbside pickup? We'll text you clear instructions on exactly where to meet. Either way, you'll be on the road within minutes of grabbing your bags.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Early Flights, Late Arrivals — We Run 24/7
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    A 4:30 AM departure or a midnight arrival is no problem. We operate 24 hours a day, 365 days a year. Pre-book your ride and sleep easy knowing your car is confirmed, your driver is vetted, and your pickup time is guaranteed.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Travel in a 2025 Audi Q7 Luxury SUV
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Our flagship vehicle is a 2025 Audi Q7 Lux Black SUV — immaculately detailed before every trip, with room for passengers and luggage alike, climate control, complimentary bottled water, and a whisper-quiet ride. It's the difference between getting to the airport and arriving at the airport.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Flat-Rate Pricing, No Surprises
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Airport transfers to and from most San Antonio locations typically start around $75–$100 depending on distance. Your rate is quoted upfront and it doesn't change — no surge pricing, no hidden fees. We also provide transfers to <Link to="/san-antonio-to-austin" className="text-primary hover:underline">Austin-Bergstrom International Airport (AUS)</Link> for travelers flying in or out of Austin.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Who We Serve
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Business executives heading to meetings, families starting a vacation, military families arriving for <Link to="/military-graduation-transportation" className="text-primary hover:underline">graduations at Lackland AFB</Link>, and frequent flyers who are done gambling on rideshares. We serve pickups and drop-offs throughout San Antonio, Stone Oak, Alamo Heights, The Dominion, Boerne, New Braunfels, Schertz, and Fair Oaks Ranch.
                  </p>
                </div>

                {/* Bottom CTA */}
                <div className="text-center py-12 border-t border-border/50">
                  <p className="text-xl md:text-2xl text-foreground font-semibold mb-6">
                    Book your SAT airport transfer now — call or text 210-296-7409, or reserve online. Available 24/7.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        Call 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Reserve Online
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AirportTransportation;
